import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SharedHeader } from "@/components/SharedHeader";
import { Mail, Phone, Star, Megaphone, Users, TrendingUp } from "lucide-react";

const SponsorsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sponsorshipTiers = [
    {
      name: "GOLD SPONSOR",
      price: "$5,000/month",
      color: "#7a6200",
      features: [
        "Premium homepage banner placement",
        "Logo on all event pages",
        "Featured sponsor at tournaments",
        "Email newsletter inclusion",
        "Social media promotion",
        "VIP event access",
        "Custom branded events",
        "Analytics dashboard access"
      ]
    },
    {
      name: "SILVER SPONSOR",
      price: "$2,500/month",
      color: "#A1A1AA",
      features: [
        "Homepage sidebar placement",
        "Logo on event listings",
        "Sponsor mention at events",
        "Monthly newsletter feature",
        "Social media mentions",
        "Event tickets included"
      ]
    },
    {
      name: "BRONZE SPONSOR",
      price: "$1,000/month",
      color: "#CD7F32",
      features: [
        "Sponsor directory listing",
        "Logo on sponsors page",
        "Quarterly newsletter mention",
        "Event discount codes"
      ]
    }
  ];

  const adPlacements = [
    {
      name: "Homepage Banner",
      size: "728x90",
      price: "$500/week",
      impressions: "~50,000/week"
    },
    {
      name: "Sidebar Ad",
      size: "300x250",
      price: "$250/week",
      impressions: "~30,000/week"
    },
    {
      name: "Event Page Banner",
      size: "468x60",
      price: "$150/week",
      impressions: "~15,000/week"
    },
    {
      name: "Newsletter Sponsor",
      size: "600x200",
      price: "$300/send",
      impressions: "~10,000 subscribers"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="sponsors-page">
      {/* Shared Header */}
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Hero Section */}
      <section className="pt-16 py-16 px-4 relative">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: "radial-gradient(ellipse at center, rgba(122, 98, 0, 0.3) 0%, transparent 60%)"
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10 text-center pt-8">
          <h2 className="font-bebas text-5xl md:text-6xl tracking-wider text-white">
            PARTNER WITH <span className="gold-text">BOARD2BOSTON™</span>
          </h2>
          <p className="mt-6 text-[#A1A1AA] font-ubuntu text-xl max-w-3xl mx-auto">
            Reach the most engaged gaming community. Our platform connects passionate card and board game enthusiasts eager to discover new products, services, and venues.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <Users className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
              <p className="font-bebas text-4xl text-white">10K+</p>
              <p className="text-[#A1A1AA] font-ubuntu text-sm">Active Members</p>
            </div>
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
              <p className="font-bebas text-4xl text-white">50K+</p>
              <p className="text-[#A1A1AA] font-ubuntu text-sm">Monthly Page Views</p>
            </div>
            <div className="text-center">
              <Star className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
              <p className="font-bebas text-4xl text-white">500+</p>
              <p className="text-[#A1A1AA] font-ubuntu text-sm">Events Per Month</p>
            </div>
            <div className="text-center">
              <Megaphone className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
              <p className="font-bebas text-4xl text-white">85%</p>
              <p className="text-[#A1A1AA] font-ubuntu text-sm">Email Open Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8 text-center">
            SPONSORSHIP PACKAGES
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sponsorshipTiers.map((tier, i) => (
              <Card 
                key={i}
                className="bg-[#0A0A0A] border-2 hover:shadow-lg transition-shadow"
                style={{ borderColor: tier.color }}
              >
                <CardHeader className="text-center border-b border-white/10">
                  <CardTitle className="font-bebas text-3xl tracking-wider" style={{ color: tier.color }}>
                    {tier.name}
                  </CardTitle>
                  <p className="font-bebas text-4xl text-white mt-2">{tier.price}</p>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <Star className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: tier.color }} />
                        <span className="text-[#A1A1AA] font-ubuntu text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="btn-gold w-full mt-6" data-testid={`sponsor-${tier.name.toLowerCase().replace(' ', '-')}`}>
                    GET STARTED
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Placements */}
      <section className="py-16 px-4 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8 text-center">
            ADVERTISING OPTIONS
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adPlacements.map((ad, i) => (
              <Card key={i} className="bg-[#050505] border border-white/10">
                <CardContent className="p-6 text-center">
                  <h4 className="font-bebas text-xl tracking-wider text-white mb-2">{ad.name}</h4>
                  <p className="text-[#52525B] font-ubuntu text-sm mb-4">Size: {ad.size}</p>
                  <p className="font-bebas text-3xl text-[#7a6200] mb-2">{ad.price}</p>
                  <p className="text-[#A1A1AA] font-ubuntu text-sm">{ad.impressions}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Current Sponsors Placeholder */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-bebas text-3xl tracking-wider text-[#7a6200] mb-8 text-center">
            OUR SPONSORS
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center opacity-50">
            <div className="w-32 h-20 bg-[#0A0A0A] border border-white/10 rounded flex items-center justify-center">
              <span className="text-[#52525B] font-ubuntu text-sm">Your Logo Here</span>
            </div>
            <div className="w-32 h-20 bg-[#0A0A0A] border border-white/10 rounded flex items-center justify-center">
              <span className="text-[#52525B] font-ubuntu text-sm">Your Logo Here</span>
            </div>
            <div className="w-32 h-20 bg-[#0A0A0A] border border-white/10 rounded flex items-center justify-center">
              <span className="text-[#52525B] font-ubuntu text-sm">Your Logo Here</span>
            </div>
            <div className="w-32 h-20 bg-[#0A0A0A] border border-white/10 rounded flex items-center justify-center">
              <span className="text-[#52525B] font-ubuntu text-sm">Your Logo Here</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-bebas text-3xl tracking-wider text-white mb-6">
            READY TO ADVERTISE?
          </h3>
          <p className="text-[#A1A1AA] font-ubuntu mb-8">
            Contact our partnerships team to discuss custom sponsorship opportunities and advertising packages.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <a 
              href="mailto:713consulting@gmail.com"
              className="flex items-center gap-2 text-[#7a6200] hover:text-[#C6A300] transition-colors font-ubuntu"
            >
              <Mail className="w-5 h-5" />
              713consulting@gmail.com
            </a>
            <a 
              href="tel:+1-713-555-0123"
              className="flex items-center gap-2 text-[#7a6200] hover:text-[#C6A300] transition-colors font-ubuntu"
            >
              <Phone className="w-5 h-5" />
              (713) 555-0123
            </a>
          </div>
          <Button className="btn-gold mt-8 px-12" data-testid="contact-sponsors-btn">
            CONTACT US
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#050505] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SponsorsPage;
